import React, { useRef, useState } from "react";
import { Plus, X, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api, { resolveImg } from "../lib/api";

const MAX = 5;

const PhotoUploader = ({ photos = [], onChange }) => {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const pick = () => inputRef.current?.click();

  const handleFile = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!files.length) return;
    const remaining = MAX - photos.length;
    if (remaining <= 0) {
      toast.error(`Maximum ${MAX} photos.`);
      return;
    }
    const toUpload = files.slice(0, remaining);
    if (files.length > remaining) {
      toast.error(`Seulement ${remaining} photo(s) ajoutée(s) (max ${MAX}).`);
    }
    setUploading(true);
    let added = 0;
    let lastPhotos = null;
    for (const file of toUpload) {
      try {
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await api.post("/profile/photos", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        lastPhotos = data.photos;
        added += 1;
      } catch (err) {
        toast.error((err.response?.data?.detail || "Échec de l'envoi d'une photo.").replace("PHOTO_MISMATCH: ", ""));
      }
    }
    if (lastPhotos) onChange(lastPhotos);
    if (added > 0) toast.success(added > 1 ? `${added} photos ajoutées !` : "Photo ajoutée !");
    setUploading(false);
  };

  const remove = async (url) => {
    try {
      const { data } = await api.delete(`/profile/photos`, { params: { url } });
      onChange(data.photos);
    } catch (err) {
      toast.error("Suppression impossible.");
    }
  };

  return (
    <div>
      <div className="grid grid-cols-3 gap-3">
        {photos.map((url, i) => (
          <div key={url} data-testid={`photo-slot-${i}`} className="relative aspect-[3/4] rounded-2xl overflow-hidden bg-gray-100 border border-kora-line">
            <img src={resolveImg(url)} alt={`Photo ${i + 1}`} className="w-full h-full object-cover" />
            {i === 0 && (
              <span className="absolute top-1.5 left-1.5 bg-kora-terracotta text-white text-[10px] font-bold px-2 py-0.5 rounded-full">Principale</span>
            )}
            <button
              type="button"
              data-testid={`remove-photo-${i}`}
              onClick={() => remove(url)}
              className="absolute top-1.5 right-1.5 w-6 h-6 rounded-full bg-black/50 text-white flex items-center justify-center"
            >
              <X size={14} />
            </button>
          </div>
        ))}
        {photos.length < MAX && (
          <button
            type="button"
            data-testid="add-photo-btn"
            onClick={pick}
            disabled={uploading}
            className="aspect-[3/4] rounded-2xl border-2 border-dashed border-kora-line flex flex-col items-center justify-center text-kora-muted hover:border-kora-terracotta hover:text-kora-terracotta transition-colors disabled:opacity-60"
          >
            {uploading ? <Loader2 size={22} className="animate-spin" /> : <Plus size={22} />}
            <span className="text-[11px] font-medium mt-1">{uploading ? "Envoi…" : "Ajouter"}</span>
          </button>
        )}
      </div>
      <p className="text-xs text-kora-muted mt-2">Téléchargez 1 photo avec votre visage bien visible. Maximum {MAX} photos. La première est votre photo principale.</p>
      <input ref={inputRef} type="file" accept="image/*" multiple onChange={handleFile} className="hidden" data-testid="photo-file-input" />
    </div>
  );
};

export default PhotoUploader;
